import { amorphicStatic, supertypeClass } from '@haventech/amorphic';
import { promises as fsPromises } from 'fs';
import { Response } from 'express';
import { execFile } from 'child_process';
import * as util from 'util';
import * as exiftool from 'dist-exiftool';
import rp from 'request-promise-native';
import { Utils } from '../Utils';
import { DownloadFileTypes, FileDownloadModes, StreamableDoc } from '../interfaces/CommonTypes';
import { RedisCache } from '../RedisCache';
import { LocationLocalitiesHandler } from '../LocationLocalitiesHandler';
import { Deferred } from '../interfaces/Deferred';
import { Location } from '../interfaces/Location';
import _ from 'lodash';
import os from 'os';
import * as crypto from 'crypto';
import * as fs from 'fs';
import { FlexibleObj } from '../interfaces/FlexibleObj';
import { file } from 'tmp';
import * as seedrandom from 'seedrandom';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';

enum GoogleMapsErrorType {
  ERROR,
  INFO,
}

interface GoogleMapsError {
  type: GoogleMapsErrorType;
  // Given a blank type because "Google does not guarantee this field to be always present, and its content is subject to change."
  // https://developers.google.com/maps/documentation/timezone/intro#Responses
  data;
}

// enum derived from https://developers.google.com/maps/documentation/timezone/intro#Responses
enum GoogleMapsResponseType {
  OK = 'OK',
  ZERO_RESULTS = 'ZERO_RESULTS',
  INVALID_REQUEST = 'INVALID_REQUEST',
  OVER_DAILY_LIMIT = 'OVER_DAILY_LIMIT',
  OVER_QUERY_LIMIT = 'OVER_QUERY_LIMIT',
  REQUEST_DENIED = 'REQUEST_DENIED',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

@supertypeClass({ toClient: false, toServer: false })
export class CommonUtilsBackend {
  static readonly DOCUSIGN_REDIRECT_CLIENT_USER_ID: number = 1000;
  private static readonly annualRegex = /annual/i;
  private static readonly monthRegex = /month/i;

  static _listeningToShutdownSignals: boolean = false;
  static _shutdownHandlers: any[] = [];
  static _geoLocationUrl: string =
    'https://maps.googleapis.com/maps/api/geocode/json?components=country:US|postal_code:';
  static _geoTimezoneUrl: string = 'https://maps.googleapis.com/maps/api/timezone/json?location=';
  static _timeZoneAPIname = 'GoogleMapsTimezoneAPIKey';
  static _geoLocationAPIname = 'GoogleMapsAPIKey';

  static async downloadFile(
    response: Response,
    fileOptions: StreamableDoc,
    viewFile: boolean,
    addTitle = true
  ): Promise<void> {
    let headers: { [name: string]: string | number };
    let filePath: string;
    let fileNameWithExt: string;

    // @NOTE this is a quick fix to unblock agents who cant esign
    addTitle = false;

    try {
      const now = new Date();
      fileNameWithExt = fileOptions.fileNameWithExtension;

      // unsure uniquiness: <timestamp>-<uuid>-<file>
      const localFileName = `${now.getTime()}-${fileOptions._id || Utils.generateUuidV4()}-${fileNameWithExt}`;

      const downloadableFile = await CommonUtilsBackend.createDownloadableFile(
        fileOptions.type as DownloadFileTypes,
        fileNameWithExt,
        (fileOptions.docS3 && fileOptions.docS3.toString()) || fileOptions.doc,
        viewFile ? FileDownloadModes.VIEW : FileDownloadModes.DOWNLOAD,
        localFileName,
        addTitle
      );
      headers = downloadableFile.headers;
      filePath = downloadableFile.filePath;
    } catch (error) {
      response.writeHead(404, {
        'Content-Type': 'text/plain',
      });

      response.end('Not found');

      LoggerSingleton.getInstance().error(
        {
          module: 'CommonUtilsBackend',
          function: 'downloadFile',
          category: Category.request,

          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
          },
        },
        `Failed to create file using a name ${fileNameWithExt}`
      );
      return;
    }

    response.sendFile(filePath, { headers }, (error) => {
      if (error) {
        LoggerSingleton.getInstance().error(
          {
            module: 'CommonUtilsBackend',
            function: 'downloadFile',
            category: Category.request,

            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
            },
          },
          'Failed'
        );
      } else {
        LoggerSingleton.getInstance().info(
          {
            module: 'CommonUtilsBackend',
            function: 'downloadFile',
            category: Category.request,
          },
          'Done'
        );
      }

      // @source https://linuxhint.com/get_filea_metadata_exif_tool/
      if (fileOptions.type === DownloadFileTypes.PDF && addTitle) {
        CommonUtilsBackend.deleteFiles([filePath, `${filePath}_original`]);
      } else {
        CommonUtilsBackend.deleteFiles([filePath]);
      }
    });
  }

  /**
   *
   * @param {POLICY_DOC_TYPES} fileType We use file types from POLICY_DOC_TYPES constant
   * @param fileNameWithExt human readable name that the user will see in the browser
   * @param content
   * @param downloadMode 'attachment' | 'inline', "attachment" tells Browser to download instead of opening
   * @param localFilename unique name of the file used when writing file to file system
   */
  private static async createDownloadableFile(
    fileType: DownloadFileTypes,
    fileNameWithExt: string,
    content: string,
    downloadMode = FileDownloadModes.DOWNLOAD,
    localFilename?: string,
    addTitle = true
  ): Promise<{ headers: { [name: string]: string | number }; filePath: string }> {
    let hasCorrectExtension = /\.(pdf|csv|jpeg|html)$/.test(fileNameWithExt);

    let contentType: string;
    let decodedData: Buffer | string = content;
    switch (fileType) {
      case DownloadFileTypes.PDF:
        contentType = 'application/pdf';
        decodedData = Buffer.from(content, 'base64');
        break;
      case DownloadFileTypes.CSV:
        contentType = 'text/plain';
        break;
      case DownloadFileTypes.JPEG:
        contentType = 'application/jpeg';
        decodedData = Buffer.from(content, 'base64');
        break;
      case DownloadFileTypes.HTML:
        contentType = 'text/html';
        break;
      default:
        hasCorrectExtension = false;
    }

    if (!hasCorrectExtension) {
      throw new Error(`We only create pdf, csv, jpeg, or html files, requested file is ${fileNameWithExt}`);
    }

    const filePath = CommonUtilsBackend.getWritableFilePath(localFilename || fileNameWithExt);

    let fileSize = 0;
    try {
      await fsPromises.writeFile(filePath, decodedData);
      if (fileType === DownloadFileTypes.PDF && addTitle) {
        await CommonUtilsBackend.addTitle(fileNameWithExt, filePath);
      }

      const stat = await fsPromises.stat(filePath);
      fileSize = stat.size;
    } catch (e) {
      throw new Error(`Failed to create file - ${e && e.message}`);
    }

    const headers = {
      'Content-Type': contentType,
      'Content-Disposition': `${downloadMode}; filename=${fileNameWithExt}`,
      'Content-Length': fileSize,
    };

    return { headers, filePath };
  }

  /**
   * Set a title on the file so that it shows in the browser instead of "xhr"
   * @throws an error if filePath point to a file that does not exists
   */
  private static async addTitle(fileNameWithExt: string, filePath: string): Promise<void> {
    const pdfTitle = `-title=${fileNameWithExt}`;
    await util.promisify(execFile)(exiftool, [pdfTitle, filePath]);
  }

  static async deleteFiles(filePaths: Array<string>): Promise<void> {
    for (const fileToDelete of filePaths) {
      try {
        await fsPromises.unlink(fileToDelete);
      } catch (e) {
        LoggerSingleton.getInstance().error(
          {
            module: 'CommonUtilsBackend',
            function: 'deleteFiles',
            category: Category.request,

            error: {
              name: e?.name,
              message: e?.message,
              stack: e?.stack,
              isHumanRelated: false,
            },
          },
          'file could not be deleted or has been already deleted'
        );
      }
    }
  }

  static sendSlackMessage(message, webhook) {
    const uri: string = webhook;
    const headers = {
      'Content-Type': 'application/json',
      ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
    };
    const data = { text: message };
    const options = {
      url: uri,
      body: data,
      json: true,
      timeout: 120000,
      headers: headers,
    };
    try {
      return rp.post(options);
    } catch (e) {
      LoggerSingleton.getInstance().error(
        {
          module: 'CoverpathUtils',
          function: 'sendSlackMessage',
          category: Category.request,

          error: {
            name: e?.name,
            message: e?.message,
            stack: e?.stack,
            isHumanRelated: false,
            data: {
              webhook: webhook,
              data: data,
            },
          },
        },
        'Error sending slack message'
      );
    }
  }

  // sequenceNumber, starting from 1, indicates the order in the sequence of random number list
  static getRandomNumber(sequenceNumber?: number, seed?: string): number {
    const randomNumberGenerator = seedrandom(seed); //seedrandom module is used here
    for (let i = 1; i < (sequenceNumber || 1); i++) {
      randomNumberGenerator();
    }
    return randomNumberGenerator();
  }

  static keyForGeoLocationAPI(): string {
    const key = CommonUtilsBackend._geoLocationAPIname + '-' + amorphicStatic.config.nconf.get('APPLICATION');
    let googleMapsGeoLocationAPIKey = amorphicStatic.config.nconf.get(key);

    if (!googleMapsGeoLocationAPIKey) {
      LoggerSingleton.getInstance().warn(
        {
          module: 'Utils',
          function: 'getGeoLocationAPIKey',
          category: Category.request,

          data: {
            keyname: googleMapsGeoLocationAPIKey,
          },
        },
        'Unable to look up Google Geolocation API key'
      );
      googleMapsGeoLocationAPIKey = amorphicStatic.config.nconf.get(CommonUtilsBackend._geoLocationAPIname);
    }

    return googleMapsGeoLocationAPIKey;
  }

  static formatFiveDigitZipcode(zipcodeToFormat: string): string {
    if (!zipcodeToFormat) {
      return '';
    }

    if (zipcodeToFormat.length === 5) {
      return zipcodeToFormat;
    }

    return zipcodeToFormat.slice(0, 5);
  }

  static publicGetLocationByZip(zipcode?: string, usePostcodeLocalities?: boolean): Promise<Location | null> {
    if (!Utils.isValidZipcode(zipcode)) {
      LoggerSingleton.getInstance().info(
        {
          module: 'HITController',
          function: 'publicGetLocationByZip',
          category: Category.request,

          data: {
            zipcode: zipcode,
          },
        },
        'Invalid ZIP Code'
      );

      return null;
    }

    const zipcodeFiveDigit = CommonUtilsBackend.formatFiveDigitZipcode(zipcode);

    if (zipcodeFiveDigit === '01111') {
      const mockDefaultLocation = {
        city: 'Springfield',
        stateCode: 'MA',
        lat: 42.117339,
        lng: -72.5481187,
        timezone: 'America/New_York',
        county: 'Hampden',
      };

      return Promise.resolve(mockDefaultLocation);
    }

    const defaultHandler = {
      getLocationInfo: function (deferred: Deferred) {
        let dataToCache = {};

        LoggerSingleton.getInstance().info(
          {
            module: 'HITController',
            function: 'publicGetLocationByZip',
            category: Category.request,

            data: {
              zipcode: zipcodeFiveDigit,
            },
          },
          'No Cached Zipcode Data - Fetching Data from Google Geo API'
        );

        return CommonUtilsBackend.getGeolocation(zipcodeFiveDigit, usePostcodeLocalities)
          .then((locData) => getTimezone(locData))
          .then((tz) => resolveLocationFetch.call(this, tz))
          .catch((err) => rejectLocationFetch(err));

        function getTimezone(location) {
          dataToCache = location;
          return CommonUtilsBackend.getTimezone(location.lat, location.lng);
        }

        function resolveLocationFetch(timezone) {
          dataToCache['timezone'] = timezone;
          LoggerSingleton.getInstance().info(
            {
              module: 'HITController',
              function: 'publicGetLocationByZip',
              category: Category.request,

              data: {
                dataToCache: dataToCache,
                zipcode: zipcodeFiveDigit,
              },
            },
            'Successfully Fetched City/State/Timezone data for ' + zipcodeFiveDigit
          );

          return deferred.resolve(dataToCache);
        }

        function rejectLocationFetch(err: GoogleMapsError) {
          return deferred.reject(err);
        }
      },
      returnLocationInfo: function (cachedLocationData: Location) {
        if (!isValidCityState(cachedLocationData)) {
          return;
        }

        return cachedLocationData;

        function isValidCityState(locationData) {
          return locationData.stateCode && locationData.city;
        }
      },
    };

    /*
            Haven app should use LocationLocalitiesHandler instead for simpler logic, otherwise
            all other apps will use the original locationHandler - now named defaultHandler
        */
    const locationHandler = usePostcodeLocalities ? new LocationLocalitiesHandler(zipcodeFiveDigit) : defaultHandler;

    return fetchByZip.call(this, zipcodeFiveDigit);

    function fetchByZip(zipcodeToFetch) {
      const zipcodeKey = 'zipcode-' + zipcodeToFetch;

      return new RedisCache()
        .fetchFromCache(zipcodeKey, (cached) => locationHandler.getLocationInfo(cached))
        .then((loc) => locationHandler.returnLocationInfo(loc))
        .catch((error: GoogleMapsError): void => {
          if (error.type !== GoogleMapsErrorType.INFO) {
            LoggerSingleton.getInstance().error({
              module: 'Utils',
              function: 'fetchByZip',
              category: Category.request,

              error: {
                isHumanRelated: false,
                data: { data: error.data },
              },
            });
          } else {
            LoggerSingleton.getInstance().info(
              {
                module: 'Utils',
                function: 'fetchByZip',
                category: Category.request,

                data: error.data,
              },
              'No results from GoogleMaps'
            );
          }
        });
    }
  }

  // static getGeolocation(zip: string, usePostcodeLocalities?: boolean): Promise<Location> {
  static getGeolocation(zip: string, usePostcodeLocalities?: boolean | string): any {
    // @TODO this is incompatible with new version of util
    if (!zip) {
      return null;
    }

    const zipcodeFiveDigit = CommonUtilsBackend.formatFiveDigitZipcode(zip);
    let geoLocationAPIUrl = CommonUtilsBackend._geoLocationUrl + zipcodeFiveDigit;

    LoggerSingleton.getInstance().debug(
      {
        module: 'Utils',
        function: 'getGeolocation',
        category: Category.request,

        data: {
          zipcode: zipcodeFiveDigit,
        },
      },
      'Making ZIP Google API Request: ' + geoLocationAPIUrl
    );

    if (CommonUtilsBackend.keyForGeoLocationAPI()) {
      geoLocationAPIUrl += '&key=' + CommonUtilsBackend.keyForGeoLocationAPI(); // Add API Key for Google Request.
    }

    return rp(geoLocationAPIUrl).then(parseResponse).catch(handleBadResponse);

    function parseResponse(body) {
      const parsedBody = JSON.parse(body);

      if (parsedBody.status !== GoogleMapsResponseType.OK) {
        throw parsedBody;
      }

      const geoLocation: Location = {
        city: null,
        stateCode: null,
        lat: null,
        lng: null,
        timezone: null,
        county: null,
      };

      LoggerSingleton.getInstance().info(
        {
          module: 'Utils',
          function: 'getGeolocation',
          category: Category.request,

          data: {
            zipcode: zipcodeFiveDigit,
            body: parsedBody,
          },
        },
        'Retrieved geolocation data from Google API for ZIP: ' + zipcodeFiveDigit
      );

      const loc = getCityCountyStateCountry(parsedBody);

      if (loc.country === 'US') {
        geoLocation.stateCode = loc.state;
        geoLocation.city = loc.city;
        geoLocation.county = loc.county;
      } else {
        LoggerSingleton.getInstance().info(
          {
            module: 'Utils',
            function: 'getGeolocation',
            category: Category.request,

            data: {
              zipcode: zipcodeFiveDigit,
            },
          },
          'ZIP is valid code but not within USA'
        );
      }

      geoLocation.lat = getLatitude(parsedBody);
      geoLocation.lng = getLongitude(parsedBody);

      return geoLocation;
    }

    function getCityCountyStateCountry(body) {
      const loc = {
        city: null,
        state: null,
        country: null,
        county: null,
      };

      const locationRes = body && body.results[0];

      if (locationRes) {
        locationRes.address_components.forEach((element) => {
          if (element.types.indexOf('country') > -1) {
            loc.country = element.short_name;
          } else if (element.types.indexOf('administrative_area_level_1') > -1) {
            loc.state = element.short_name;
          }
          // adding county for conversion form
          if (element.types.indexOf('administrative_area_level_2') > -1) {
            loc.county = element.short_name;
          }
        });

        if (loc.country === 'US' && loc.state) {
          // Get city list from postcode_localities param
          const localities = locationRes.postcode_localities;
          // Get city from formatted_address
          const addressTokens = locationRes.formatted_address.split(',');

          /*
                        usePostcodeLocalities is an optional flag which is set to true for the haven app
                        for new functionality which requires the postcode_localities field
                        if available
                    */
          if (usePostcodeLocalities && localities) {
            loc.city = locationRes.postcode_localities;
          } else if (addressTokens.length >= 3) {
            loc.city = addressTokens.reverse()[2].trim();
          }
        }
      }

      return loc;
    }

    function getLatitude(body): number {
      return body.results[0].geometry.location.lat;
    }

    function getLongitude(body): number {
      return body.results[0].geometry.location.lng;
    }

    function handleBadResponse(errorResponse): Promise<GoogleMapsError> {
      const errorObject = {
        data: errorResponse,
        type:
          errorResponse.status === GoogleMapsResponseType.ZERO_RESULTS
            ? GoogleMapsErrorType.INFO
            : GoogleMapsErrorType.ERROR,
      };

      return Promise.reject(errorObject);
    }
  }

  static getTimezone(latitude: string, longitude: string): Promise<string> {
    const key = CommonUtilsBackend._timeZoneAPIname + '-' + amorphicStatic.config.nconf.get('APPLICATION');
    let googleMapsTimezoneAPIKey = amorphicStatic.config.nconf.get(key);

    if (!googleMapsTimezoneAPIKey) {
      LoggerSingleton.getInstance().warn(
        {
          module: 'CommonUtilsBackend',
          function: 'getTimezone',
          category: Category.request,

          data: {
            keyname: key,
          },
        },
        'Unable to look up Google API key'
      );
      googleMapsTimezoneAPIKey = amorphicStatic.config.nconf.get(CommonUtilsBackend._timeZoneAPIname);
    }

    const secs = new Date().getTime() / 1e3;
    let url = CommonUtilsBackend._geoTimezoneUrl + (latitude + ',' + longitude) + '&timestamp=' + secs;

    if (googleMapsTimezoneAPIKey) {
      url += `&key=${googleMapsTimezoneAPIKey}`;
    }

    return rp(url)
      .then((body) => CommonUtilsBackend.parseTimezone(latitude, longitude, body))
      .catch((err) => {
        const errorObject = {
          data: err,
          type:
            err.status === GoogleMapsResponseType.ZERO_RESULTS ? GoogleMapsErrorType.INFO : GoogleMapsErrorType.ERROR,
        };
        return Promise.reject(errorObject);
      });
  }

  static async getTimezoneIdByZipcode(zip: string): Promise<string> {
    const geoloc = await CommonUtilsBackend.getGeolocation(zip, false);
    return CommonUtilsBackend.getTimezone(geoloc.lat.toString(), geoloc.lng.toString());
  }

  static parseTimezone(latitude: string, longitude: string, body): string {
    const data = JSON.parse(body);

    if (data.status !== GoogleMapsResponseType.OK) {
      throw data;
    }

    LoggerSingleton.getInstance().info(
      {
        module: 'Utils',
        function: 'getTimezone',
        category: Category.request,

        data: {
          latitude: latitude,
          longitude: longitude,
          timezone: data.timeZoneId,
        },
      },
      'Retrieved timezone data from Google API for lat/lng'
    );

    return data.timeZoneId;
  }

  static ipAddress(): string {
    const ifaces = os.networkInterfaces();

    const ip = _.find(_.flatten(_.values(ifaces)), {
      family: 'IPv4',
      internal: false,
    });

    return ip ? ip.address : 'Unknown';
  }

  static listenToShutdownSignals(shutdownHandler: () => void) {
    if (shutdownHandler) {
      CommonUtilsBackend._shutdownHandlers.push(shutdownHandler);
    }

    if (CommonUtilsBackend._listeningToShutdownSignals) {
      return;
    }

    CommonUtilsBackend._listeningToShutdownSignals = true;

    const signals: any[] = ['SIGHUP', 'SIGINT', 'SIGQUIT', 'SIGABRT', 'SIGTERM'];

    signals.forEach((signal) => {
      process.on(signal, () => {
        LoggerSingleton.getInstance().info(
          {
            module: 'Utils.listenToShutdownSignals',
            function: '_listeningToShutdownSignals',
            category: Category.request,

            data: signal,
          },
          'Shutdown triggered by ' + signal
        );

        Promise.all(
          CommonUtilsBackend._shutdownHandlers.map((handler) => {
            return handler();
          })
        ).then(() => {
          setTimeout(() => {
            // Allow logs to complete
            process.exit(1);
          }, 1e3);
        });
      });
    });
  }

  static transformTemplate(templateName: string, data, ctx, options?) {
    options = options || {};

    const templatePath = options.templatePath || __dirname + '/../../workflow/templates/';

    return new Promise((resolve, reject) => {
      try {
        resolve(
          fs.readFileSync(templatePath + templateName, {
            encoding: 'utf-8',
          })
        );
      } catch (e) {
        reject(e);
      }
    }).then(buildTemplate);

    function buildTemplate(templateStr) {
      const bodyOnly = options.bodyOnly || false;
      const templStr = bodyOnly ? templateStr.match(/<body>([\s\S]*)<\/body>/im)[1] : templateStr;

      ctx = ctx || {};
      ctx.data = data;

      return _.template(templStr, {
        variable: 'ctx',
      })(ctx);
    }
  }

  static encrypt(text: string, algorithm: string, key: string): string {
    const cipher = crypto.createCipher(algorithm, key);
    const encText = cipher.update(text, 'utf8', 'base64');

    return encText + cipher.final('base64');
  }

  static decrypt(encText: string, algorithm: string, key: string): string {
    const cipher = crypto.createDecipher(algorithm, key);
    const text = cipher.update(encText, 'base64', 'utf8');

    return text + cipher.final('utf8');
  }

  static visitFiles(path: string, callback: (path, b) => void): void {
    try {
      fs.statSync(path).isDirectory() ? visitDir() : callback(path, true);
    } catch (e) {
      // Ignore, just log.
      LoggerSingleton.getInstance().error(
        {
          module: 'Utils.visitFiles',
          function: 'visitFiles',
          category: Category.request,

          error: {
            name: e?.name,
            message: e?.message,
            stack: e?.stack,
            isHumanRelated: false,
            data: {
              path: path,
            },
          },
        },
        'Visiting ' + path + ' threw error'
      );
    }

    function visitDir() {
      fs.readdirSync(path).forEach((file) => {
        CommonUtilsBackend.visitFiles(path + '/' + file, callback);
      });

      callback(path, false);
    }
  }

  static getWritableFilePath(fileName: string): string {
    // Docker only allows certain writable paths
    return `${os.tmpdir()}/${fileName}`;
  }

  static loadCSVFileAsJson(
    filePath: string,
    separator: string = ',',
    encoding: BufferEncoding = 'utf8'
  ): FlexibleObj<string>[] {
    // readFileSync actually return <string> | <Buffer>, here we are assuming is a string
    const input = fs.readFileSync(filePath, encoding) as string;
    const lines = input.split(/[\r|\n|\r\n]/);
    return CommonUtilsBackend.convertCSVArrayToJSON(lines, separator);
  }

  static convertCSVArrayToJSON(lines: string[], separator: string = ','): FlexibleObj<string>[] {
    const keys = lines.shift().trim().split(separator);

    return _.reduce(
      lines,
      (output: FlexibleObj<string>[], line) => {
        if (line) {
          const obj: FlexibleObj<string> = {};
          const values = line.split(separator);
          _.each(values, function (value, index) {
            obj[keys[index]] = value;
          });
          output.push(obj);
        }
        return output;
      },
      []
    );
  }

  static convertJsonToCSVString(dataArr: any[]): string {
    const keys = _.keys(dataArr[0]);
    const result = keys.join(',\t') + '\n';

    return _.reduce(
      dataArr,
      function (result, obj) {
        _.each(keys, function (key, index) {
          if (index) {
            //skip if index === 0
            result += ',\t';
          }
          result += obj[key];
        });
        return result + '\n';
      },
      result
    );
  }

  static executionCtx() {
    return {
      _execCtx: {
        type: Object,
        isLocal: true,
      },
      get execCtx() {
        return this._execCtx || (this._execCtx = {});
      },
      get txnCtx() {
        return this.execCtx.txnCtx || (this.execCtx.txnCtx = {});
      },
      get txn() {
        return this.txnCtx.txn || this.beginTxn().txn;
      },
      beginTxn: function (signalCustomer) {
        this.execCtx.txnCtx = {
          txn: amorphicStatic.begin(),
        };

        if (signalCustomer) {
          this.execCtx.txnCtx.txn.touchTop = true;
        }

        return this.execCtx.txnCtx;
      },
      endTxn: function () {
        const txn = this.txn;

        this.execCtx.txnCtx = null;

        return amorphicStatic.end(txn);
      },
      setDirtyTxn: function (obj, onlyIfChanged, cascade) {
        obj = obj || this;

        return obj.setDirty(this.txn, onlyIfChanged, cascade);
      },
      addDelTxnCallback: function (callback) {
        const callbacks = this.txnCtx.delTxnCallbacks || (this.txnCtx.delTxnCallbacks = []);

        callbacks.push(callback);

        if (callbacks.length === 1) {
          this.txn.preSave = (txn) => {
            return Promise.all(
              callbacks.map((cb) => {
                return cb(txn);
              })
            );
          };
        }
      },
    };
  }

  static readFile(filePath: string, encoding?: BufferEncoding): Promise<string | Buffer> {
    return new Promise<string | Buffer>((resolve, reject) => {
      fs.readFile(filePath, { encoding }, (err: Error, data: string | Buffer) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(data);
      });
    });
  }

  static createTempFileAsync(options): Promise<{ name; fileDescriptor }> {
    return new Promise((resolve, reject) => {
      file(options, (err, name, fileDescriptor) => {
        if (err) {
          LoggerSingleton.getInstance().error(
            {
              module: 'Utils',
              function: 'createTempFileAsync',
              category: Category.request,

              error: {
                name: err?.name,
                message: err?.message,
                stack: err?.stack,
                isHumanRelated: false,
              },
            },
            err
          );

          reject(err);
          return;
        }

        resolve({ name: name, fileDescriptor: fileDescriptor });
      });
    });
  }

  static fsClose(fd): Promise<boolean> {
    return new Promise((resolve, reject) => {
      fs.close(fd, (err) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(true);
      });
    });
  }

  static fsWriteStream(fileName, options, fileBuffer): Promise<any> {
    return new Promise((resolve, reject) => {
      const fileStream = fs.createWriteStream(fileName, options);

      fileStream
        .on('error', (err) => {
          LoggerSingleton.getInstance().error(
            {
              module: 'Utils',
              function: 'fsWriteStream',
              category: Category.request,

              error: {
                name: err?.name,
                message: err?.message,
                stack: err?.stack,
                isHumanRelated: false,
              },
            },
            err
          );
          reject(err);
        })
        .on('finish', () => {
          resolve(fileName);
        });

      fileStream.write(fileBuffer);
      fileStream.end();
    });
  }
}
